import { useApolloClient } from '@apollo/client/react';
import { SeekApiPayload } from '@seek/indie-api-types';
import {
  Box,
  Button,
  Column,
  Columns,
  IconChevron,
  IconDownload,
  IconSearch,
  Inline,
  MenuItemCheckbox,
  MenuRenderer,
  Stack,
  Text,
  TextField,
} from 'braid-design-system';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { InfiniteScrollingList } from '#src/components/InfiniteScrollingList/InfiniteScrollingList.tsx';
import { usePaginationDirectionState } from '#src/components/PaginationDirectionPicker/PaginationDirectionPicker.tsx';
import { Query } from '#src/components/Query/Query.tsx';
import { useEnvironmentConfig } from '#src/hooks/environment.tsx';
import type {
  RelatedHirersQuery,
  RelatedHirersQueryVariables,
} from '#src/types/generated/graphql.ts';

import { DISPLAY_LABEL_FOR_API_RELATIONSHIP, Hirer } from '../Hirer/Hirer.tsx';

import { useTracking } from '#src/hooks/tracking/TrackingProvider.tsx';
import { downloadCsv } from './downloadCsv.ts';
import { RELATED_HIRERS } from './queries.ts';
import { useLocation } from 'react-router';
import { vars } from 'braid-design-system/css';

const RELATIONSHIP_TYPES = SeekApiPayload.RelationshipTypeCode.alternatives.map(
  ({ value }) => value,
);

const relationshipTypesLabel = (
  types: SeekApiPayload.RelationshipTypeCode[],
) => {
  const displayTypes = types.map(
    (type) => DISPLAY_LABEL_FOR_API_RELATIONSHIP[type],
  );

  if (displayTypes.length === 1) {
    return displayTypes[0];
  }

  if (!displayTypes.length) {
    return 'No relationship types';
  }

  if (displayTypes.length === RELATIONSHIP_TYPES.length) {
    return 'All relationship types';
  }

  return `${displayTypes.length} relationship types`;
};

const HirerInput = ({
  onChange,
  initialValue,
}: {
  onChange: (value: string) => void;
  initialValue: string | null;
}) => {
  const [dirty, setDirty] = useState(false);
  const [nameSearch, setNameSearch] = useState(initialValue ?? '');
  const [debouncedNameSearch] = useDebounce(nameSearch, 500);
  const trimmedNameSearch = debouncedNameSearch.trim();

  useEffect(() => {
    if (initialValue && !dirty) {
      setNameSearch(initialValue);
      setDirty(true);
      onChange(initialValue);
    }
  }, [initialValue, dirty, onChange]);

  useEffect(() => {
    onChange(trimmedNameSearch);
  }, [onChange, trimmedNameSearch]);

  return (
    <TextField
      aria-label="Hirer name"
      id="hirerListNameSearch"
      onChange={(event) => {
        if (event.currentTarget.value.length > 0) {
          setDirty(true);
        }
        setNameSearch(event.currentTarget.value);
      }}
      placeholder="Acme Corp or seekAnz:organization:seek:abcdef"
      label="Search by name or ID"
      icon={<IconSearch />}
      value={nameSearch}
    />
  );
};

export const HirerList = () => {
  const location = useLocation();

  // We don't modify this state at the moment but we utilise its helpers.
  const { firstOrLast, processPageInfo } =
    usePaginationDirectionState('FORWARDS');

  const [nameSearch, setNameSearch] = useState('');

  const [relationshipTypeCodes, setEventTypeCodes] =
    useState<SeekApiPayload.RelationshipTypeCode[]>(RELATIONSHIP_TYPES);

  const apolloClient = useApolloClient();
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  const { seekAnzSchemeId } = useEnvironmentConfig();

  const { trackLink } = useTracking();
  const trackLinkRef = useRef(trackLink);

  useEffect(() => {
    if (nameSearch) {
      trackLinkRef.current({
        eventName: 'developer_site_lookup_succeeded',
        data: {
          lookupString: nameSearch,
        },
      });
    }
  }, [nameSearch]);

  const doNumericSearch = /^\d+$/.test(nameSearch);
  const doOidSearch = nameSearch.startsWith(`${seekAnzSchemeId}:`);

  const baseParams: Omit<RelatedHirersQueryVariables, 'after' | 'first'> = {
    filter: {
      nameSearch: nameSearch || null,
      relationshipTypeCodes,
    },
    schemeId: seekAnzSchemeId,
    doNumericSearch,
    numericId: doNumericSearch ? Number(nameSearch) : -999,
    doOidSearch,
    oid: doOidSearch ? nameSearch : '',
  };

  return (
    <Stack space="large">
      <Columns space="gutter" collapseBelow="desktop" alignY="bottom">
        <Column>
          <HirerInput
            onChange={setNameSearch}
            initialValue={new URLSearchParams(location.search).get('id')}
          />
        </Column>
        <Column width="content">
          <Box
            style={{ minHeight: vars.touchableSize }}
            display="flex"
            alignItems="center"
          >
            <MenuRenderer
              align="left"
              offsetSpace="small"
              trigger={(triggerProps, { open }) => (
                <Box userSelect="none" cursor="pointer" {...triggerProps}>
                  <Text weight="medium">
                    {relationshipTypesLabel(relationshipTypeCodes)}{' '}
                    <IconChevron
                      direction={open ? 'up' : 'down'}
                      alignY="lowercase"
                    />
                  </Text>
                </Box>
              )}
            >
              {RELATIONSHIP_TYPES.map((type) => (
                <MenuItemCheckbox
                  checked={relationshipTypeCodes.includes(type)}
                  key={type}
                  onChange={() =>
                    setEventTypeCodes((existing) =>
                      existing.includes(type)
                        ? existing.filter((item) => item !== type)
                        : [...existing, type],
                    )
                  }
                >
                  {DISPLAY_LABEL_FOR_API_RELATIONSHIP[type]}
                </MenuItemCheckbox>
              ))}
            </MenuRenderer>
          </Box>
        </Column>
      </Columns>

      <Inline space="none">
        <Button
          bleed
          icon={<IconDownload />}
          loading={downloadingCsv}
          onClick={() => {
            setDownloadingCsv(true);
            downloadCsv(apolloClient, baseParams).finally(() =>
              setDownloadingCsv(false),
            );
          }}
          size="small"
          variant="soft"
        >
          {downloadingCsv
            ? 'Generating'
            : `Export ${nameSearch ? 'matching' : 'all'} hirers as CSV`}
        </Button>
      </Inline>

      <Query<RelatedHirersQuery, RelatedHirersQueryVariables>
        errorMessage="We couldn’t load your hirers."
        query={RELATED_HIRERS}
        variables={{
          ...baseParams,
          // `hiringOrganizations` has high per-query overhead so request as
          // many as possible at once
          [firstOrLast]: 100,
        }}
      >
        {({ data, fetchMore }) => {
          const { edges, pageInfo } = data.hiringOrganizations;
          const { shouldLoadMore, variables } = processPageInfo(pageInfo);

          const loadMore = () => fetchMore({ variables });

          if (data?.hiringOrganization) {
            return (
              <Stack space="large">
                <Text>Found an exact match for OID {nameSearch}:</Text>
                <Hirer hirer={data.hiringOrganization} />
              </Stack>
            );
          }

          if (data?.seekAnzAdvertiser) {
            return (
              <Stack space="large">
                <Text>
                  Found an exact match for Advertiser ID {nameSearch}:
                </Text>
                <Hirer hirer={data.seekAnzAdvertiser} />
              </Stack>
            );
          }

          return (
            <InfiniteScrollingList
              loadMore={loadMore}
              shouldLoadMore={shouldLoadMore}
              isEmpty={edges.length === 0}
              subject="hirers"
              items={edges.map(({ node }) => (
                <Hirer hirer={node} key={node.id.value} />
              ))}
            />
          );
        }}
      </Query>
    </Stack>
  );
};
